'use client';

import { useCallback, useState } from 'react';
import { useSWRConfig } from 'swr';
import { usersApi, type UserProfile } from '@/lib/api';
import type { SetBillingAddressRequest } from '@/types/billing';
import { useAuthedFetcher } from './swrConfig';

/**
 * The billing-address write (VER-389). The read rides useProfile — this hook only
 * saves and settles the shared ['user-profile'] cache with the returned profile,
 * so every consumer (the pre-flight's boolean included) agrees without a refetch.
 */
export function useSaveBillingAddress(): {
  save: (data: SetBillingAddressRequest) => Promise<UserProfile>;
  saving: boolean;
} {
  const authedFetch = useAuthedFetcher();
  const { mutate } = useSWRConfig();
  const [saving, setSaving] = useState(false);

  const save = useCallback(
    async (data: SetBillingAddressRequest): Promise<UserProfile> => {
      setSaving(true);
      try {
        const profile = await authedFetch((token) => usersApi.updateProfile(token, { billingAddress: data }));
        // Merge ONLY the billing fields onto the live cache — a consent PUT can
        // land while this request is in flight, and replacing the whole cached
        // profile with this (older) response would restore its stale consents,
        // persistently, since revalidation is off. Mirror of the toggle's rule.
        await mutate(
          ['user-profile'],
          (current: UserProfile | undefined): UserProfile =>
            current
              ? {
                  ...current,
                  billingAddress: profile.billingAddress ?? null,
                  billingAddressComplete: profile.billingAddressComplete === true,
                }
              : profile,
          { revalidate: false },
        );
        return profile;
      } finally {
        setSaving(false);
      }
    },
    [authedFetch, mutate],
  );

  return { save, saving };
}
